import PropTypes from "prop-types";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Link,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { IconAlertTriangle, IconExternalLink, IconPhoto, IconPlus, IconUpload, IconX } from "@tabler/icons";
import PageContainer from "src/components/container/PageContainer";
import ActionButton, { iconButtonSx } from "src/components/shared/ActionButton";
import PageHeader from "src/components/shared/PageHeader";
import { PAGES, imageUrl, isFile, pageUrl, shortName } from "./checks";

// The building blocks of the website content pages (services, steps, FAQs,
// testimonials, partner logos, contact details): one look, one set of states,
// and every item says where on the website it appears.

// ---------------------------------------------------------------------------
// Loading a list, with retry and a quiet refresh after each change.

export function useContentList(load) {
  const [state, setState] = useState({ loading: true, failed: false, items: null });
  const latest = useRef(0);
  const reload = useCallback(() => {
    const ticket = ++latest.current;
    setState((s) => ({ ...s, loading: true, failed: false }));
    return load()
      .then((items) => {
        if (ticket === latest.current) setState({ loading: false, failed: false, items });
      })
      .catch(() => {
        if (ticket === latest.current) setState((s) => ({ ...s, loading: false, failed: true }));
      });
  }, [load]);
  useEffect(() => {
    reload();
  }, [reload]);
  // Change one item on screen without a reload (after a switch is saved).
  const patch = useCallback((id, changes) => {
    setState((s) => ({ ...s, items: s.items?.map((item) => (item.id === id ? { ...item, ...changes } : item)) ?? null }));
  }, []);
  return { ...state, reload, patch };
}

// ---------------------------------------------------------------------------
// Where an item shows on the website, as a link.

export function WhereLink({ page, section }) {
  return (
    <Link
      href={pageUrl(page)}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontWeight: 600 }}
    >
      {PAGES[page].name}
      {section ? `, “${section}”` : ""}
      <IconExternalLink size={14} aria-hidden />
      <Box component="span" sx={visuallyHidden}>
        (opens the website in a new tab)
      </Box>
    </Link>
  );
}

WhereLink.propTypes = { page: PropTypes.oneOf(Object.keys(PAGES)).isRequired, section: PropTypes.string };

// ---------------------------------------------------------------------------
// The page: the shared header (title, what the page is for, the one primary
// action), one line saying what is live on the website, then the list.

export function ContentPage({ title, intro, where, notice, addLabel, onAdd, canAdd, children, dialogs }) {
  return (
    <PageContainer title={`${title} · Safrati CMS`} description={typeof intro === "string" ? intro : title}>
      <PageHeader
        title={title}
        subtitle={intro}
        actions={
          canAdd ? (
            <ActionButton variant="contained" startIcon={<IconPlus size={18} />} onClick={onAdd}>
              {addLabel}
            </ActionButton>
          ) : null
        }
      >
        {where && (
          <Typography variant="body2" component="div" sx={{ mt: 1, maxWidth: 760, color: "text.primary" }}>
            {where}
          </Typography>
        )}
      </PageHeader>
      {notice}
      {children}
      {dialogs}
    </PageContainer>
  );
}

ContentPage.propTypes = {
  title: PropTypes.string.isRequired,
  intro: PropTypes.node,
  where: PropTypes.node,
  notice: PropTypes.node,
  addLabel: PropTypes.string,
  onAdd: PropTypes.func,
  canAdd: PropTypes.bool,
  children: PropTypes.node,
  dialogs: PropTypes.node,
};

// "Show: All / On the FAQs page / …" for ResourceList's filters slot.
export function ShowFilter({ value, onChange, options }) {
  return (
    <TextField
      select
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{ minWidth: 220, "& .MuiInputBase-root": { minHeight: 40 } }}
      inputProps={{ "aria-label": "Show" }}
      SelectProps={{ renderValue: (v) => `Show: ${options.find((o) => o.value === v)?.label ?? v}` }}
    >
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
          {o.count != null ? ` (${o.count})` : ""}
        </MenuItem>
      ))}
    </TextField>
  );
}

ShowFilter.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string, count: PropTypes.number })).isRequired,
};

// The "Needs a look" column: what to look at before it goes on the website.
export function CheckCell({ warnings }) {
  if (!warnings?.length) {
    return (
      <Typography component="span" variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Stack spacing={0.5} sx={{ color: (t) => (t.palette.mode === "dark" ? "#F5C26B" : "#8A5A00") }}>
      {warnings.map((w) => (
        <Stack key={w} direction="row" spacing={0.75} alignItems="flex-start">
          <Box sx={{ display: "flex", mt: "2px", flexShrink: 0 }}>
            <IconAlertTriangle size={16} aria-hidden />
          </Box>
          <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: "inherit" }}>
            {w}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

CheckCell.propTypes = { warnings: PropTypes.arrayOf(PropTypes.string) };

// One line, once per page, about where these items show on the website,
// instead of repeating it on every item.
export function SiteBanner({ title, children, severity = "info" }) {
  return (
    <Alert severity={severity} sx={{ mb: 2, "& .MuiAlert-message": { width: "100%" } }}>
      {title && (
        <Typography variant="body2" fontWeight={700} component="p">
          {title}
        </Typography>
      )}
      <Typography variant="body2" component="div">
        {children}
      </Typography>
    </Alert>
  );
}

SiteBanner.propTypes = { title: PropTypes.node, children: PropTypes.node, severity: PropTypes.string };

// A labelled on/off switch that saves at once and shows it is saving.
export function SaveSwitch({ label, item, checked, onChange, disabled, hint }) {
  const [busy, setBusy] = useState(false);
  const id = useId();
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minHeight: 44 }}>
      <Switch
        id={id}
        checked={checked}
        disabled={disabled || busy}
        inputProps={{ "aria-describedby": hint ? `${id}-hint` : undefined, "aria-label": item ? `${label}: ${shortName(item)}` : undefined }}
        sx={(t) => ({
          "& .MuiSwitch-switchBase.Mui-focusVisible": {
            outline: `2px solid ${t.palette.mode === "dark" ? "#ffffff" : t.palette.primary.main}`,
            outlineOffset: "-2px",
          },
        })}
        onChange={async (e) => {
          const next = e.target.checked;
          setBusy(true);
          try {
            await onChange(next);
          } finally {
            setBusy(false);
          }
        }}
      />
      <Box component="label" htmlFor={id} sx={{ cursor: disabled ? "default" : "pointer" }}>
        <Typography variant="body2" fontWeight={600} component="span" sx={{ whiteSpace: "nowrap" }}>
          {label}
        </Typography>
        {hint && (
          <Typography id={`${id}-hint`} variant="caption" color="text.secondary" component="span" sx={{ display: "block", lineHeight: 1.3 }}>
            {hint}
          </Typography>
        )}
      </Box>
      {busy && <CircularProgress size={14} aria-label="Saving" />}
    </Stack>
  );
}

SaveSwitch.propTypes = {
  label: PropTypes.string,
  item: PropTypes.string,
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  hint: PropTypes.node,
};

export function StatusChip({ live, liveText = "Live", hiddenText = "Off" }) {
  return <Chip size="small" color={live ? "success" : "default"} variant={live ? "filled" : "outlined"} label={live ? liveText : hiddenText} />;
}

StatusChip.propTypes = { live: PropTypes.bool, liveText: PropTypes.string, hiddenText: PropTypes.string };

// ---------------------------------------------------------------------------
// In-app confirm for deletes (never window.confirm).

export function ConfirmDialog({ open, title, children, confirmLabel, onConfirm, onClose, tone = "destructive", cancelLabel = "Keep it" }) {
  const phone = useMediaQuery((t) => t.breakpoints.down("sm"));
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" fullScreen={false} aria-labelledby="confirm-title">
      <DialogTitle id="confirm-title" sx={{ fontSize: "1.125rem", fontWeight: 700, pb: 1 }}>
        {title}
      </DialogTitle>
      <DialogContent>
        <Typography component="div" color="text.secondary">
          {children}
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{ px: 3, pb: 2.5, gap: 1, flexDirection: phone ? "column-reverse" : "row", "& > :not(:first-of-type)": { ml: phone ? 0 : 1 } }}
      >
        <ActionButton variant="text" size="medium" onClick={onClose} autoFocus sx={{ width: phone ? "100%" : "auto" }}>
          {cancelLabel}
        </ActionButton>
        <ActionButton
          variant={tone === "destructive" ? "outlined" : "contained"}
          tone={tone === "destructive" ? "destructive" : undefined}
          size="medium"
          busyLabel="Working…"
          onClick={async () => {
            const ok = await onConfirm();
            if (ok !== false) onClose();
          }}
          sx={[{ width: phone ? "100%" : "auto" }, tone === "destructive" && { border: 1, borderColor: (t) => t.palette.secondary.main }]}
        >
          {confirmLabel}
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
}

ConfirmDialog.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.node,
  children: PropTypes.node,
  confirmLabel: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  tone: PropTypes.oneOf(["destructive", "primary"]),
  /** The button that closes it without acting ("Cancel" on the library and content pages). */
  cancelLabel: PropTypes.string,
};

// Holds which item a confirm is about; `ask(item)` opens it.
export function useConfirm() {
  const [item, setItem] = useState(null);
  return { item, ask: setItem, close: () => setItem(null), open: item != null };
}

// ---------------------------------------------------------------------------
// The editor: a dialog (full screen on a phone) with labelled fields, a save
// button that shows it is working, and errors that say how to fix them.

export function EditorDialog({ open, title, subtitle, onClose, onSave, saveLabel, errors, children, dirty }) {
  const phone = useMediaQuery((t) => t.breakpoints.down("sm"));
  const formRef = useRef(null);
  const [askLeave, setAskLeave] = useState(false);
  const [saving, setSaving] = useState(false);
  const errorCount = Object.keys(errors || {}).length;
  const close = () => {
    if (saving) return;
    if (dirty) setAskLeave(true);
    else onClose();
  };
  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      fullScreen={phone}
      aria-labelledby="editor-title"
      scroll="paper"
      TransitionProps={{
        // Start on the first field, not on the dialog box.
        onEntered: () =>
          formRef.current
            ?.querySelector(
              '.MuiDialogContent-root input:not([type="hidden"]):not([type="file"]):not([type="checkbox"]):not([tabindex="-1"]), .MuiDialogContent-root textarea:not([aria-hidden="true"]), .MuiDialogContent-root [role="button"][aria-haspopup], .MuiDialogContent-root [role="combobox"], .MuiDialogContent-root button'
            )
            ?.focus(),
      }}
    >
      <Box
        component="form"
        ref={formRef}
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          if (saving) return;
          setSaving(true);
          let ok;
          try {
            ok = await onSave();
          } finally {
            setSaving(false);
          }
          if (ok === false) {
            // Move to the first field with a problem.
            requestAnimationFrame(() => formRef.current?.querySelector('[aria-invalid="true"]')?.focus());
          }
        }}
        sx={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}
      >
        <DialogTitle id="editor-title" sx={{ pb: 0.5, pr: 7 }}>
          <Typography component="span" variant="h5" sx={{ display: "block", fontWeight: 700 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
          <IconButton aria-label="Close without saving" onClick={close} sx={[iconButtonSx, { position: "absolute", right: 12, top: 12 }]}>
            <IconX size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: "20px !important" }}>
          {children}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, gap: 1, flexWrap: "wrap", "& > :not(:first-of-type)": { ml: 0 } }}>
          {errorCount > 0 && (
            <Typography
              role="alert"
              variant="body2"
              sx={{ flex: { xs: "1 0 100%", sm: "1 1 auto" }, color: (t) => (t.palette.mode === "dark" ? "#FDA29B" : "#B42318"), fontWeight: 600 }}
            >
              {errorCount === 1 ? "1 field needs fixing." : `${errorCount} fields need fixing.`}
            </Typography>
          )}
          <Stack direction="row" spacing={1} sx={{ ml: "auto !important", flexWrap: "nowrap" }}>
            <ActionButton variant="text" size="medium" onClick={close}>
              Cancel
            </ActionButton>
            <ActionButton variant="contained" size="medium" type="submit" busy={saving} busyLabel="Saving…">
              {saveLabel}
            </ActionButton>
          </Stack>
        </DialogActions>
      </Box>
      <ConfirmDialog
        open={askLeave}
        title="Leave without saving?"
        confirmLabel="Leave without saving"
        onConfirm={() => {
          setAskLeave(false);
          onClose();
          return false;
        }}
        onClose={() => setAskLeave(false)}
      >
        Your changes in this form will be lost.
      </ConfirmDialog>
    </Dialog>
  );
}

EditorDialog.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saveLabel: PropTypes.string.isRequired,
  errors: PropTypes.object,
  children: PropTypes.node,
  dirty: PropTypes.bool,
};

// Form state for an editor: values, errors, and whether anything changed.
export function useForm(initial) {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [start, setStart] = useState(initial);
  const reset = useCallback((next) => {
    setValues(next);
    setStart(next);
    setErrors({});
  }, []);
  const set = useCallback((key, value) => {
    setValues((v) => ({ ...v, [key]: value }));
    setErrors((e) => {
      if (!e[key]) return e;
      const { [key]: _, ...rest } = e;
      return rest;
    });
  }, []);
  const dirty = useMemo(() => Object.keys(values).some((k) => values[k] !== start[k]), [values, start]);
  return { values, errors, setErrors, set, reset, dirty };
}

// A text field with its label above, always visible.
export function Field({ label, optional, hint, error, value, onChange, multiline, rows, maxLength, counter, type = "text", inputProps, ...rest }) {
  const id = useId();
  const length = String(value ?? "").length;
  return (
    <Box>
      <Box component="label" htmlFor={id} sx={{ display: "block", fontWeight: 600, mb: 0.75 }}>
        {label}
        {optional && (
          <Typography component="span" color="text.secondary" fontWeight={400}>
            {" "}
            (optional)
          </Typography>
        )}
      </Box>
      <TextField
        id={id}
        fullWidth
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        multiline={multiline}
        minRows={multiline ? rows || 3 : undefined}
        error={!!error}
        inputProps={{ maxLength, "aria-invalid": error ? "true" : "false", "aria-describedby": `${id}-help`, ...inputProps }}
        sx={{ "& .MuiInputBase-root": { minHeight: 48, borderRadius: "14px", ...(multiline && { alignItems: "flex-start" }) } }}
        {...rest}
      />
      <Stack id={`${id}-help`} direction="row" justifyContent="space-between" spacing={1} sx={{ mt: 0.5 }}>
        <Typography
          variant="body2"
          sx={{
            color: (t) => (error ? (t.palette.mode === "dark" ? "#FDA29B" : "#B42318") : t.palette.text.secondary),
            fontWeight: error ? 600 : 400,
          }}
        >
          {error || hint}
        </Typography>
        {counter && (
          <Typography variant="body2" color={length > counter ? "warning.dark" : "text.secondary"} sx={{ whiteSpace: "nowrap" }}>
            {length} / {counter}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

Field.propTypes = {
  label: PropTypes.node.isRequired,
  optional: PropTypes.bool,
  hint: PropTypes.node,
  error: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  maxLength: PropTypes.number,
  counter: PropTypes.number,
  type: PropTypes.string,
  inputProps: PropTypes.object,
};

export function FieldLabel({ children, optional, id, htmlFor }) {
  return (
    <Typography id={id} component={htmlFor ? "label" : "div"} htmlFor={htmlFor} fontWeight={600} sx={{ mb: 0.75, display: "block" }}>
      {children}
      {optional && (
        <Typography component="span" color="text.secondary" fontWeight={400}>
          {" "}
          (optional)
        </Typography>
      )}
    </Typography>
  );
}

FieldLabel.propTypes = { children: PropTypes.node, optional: PropTypes.bool, id: PropTypes.string, htmlFor: PropTypes.string };

export function FieldError({ children }) {
  if (!children) return null;
  return (
    <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600, color: (t) => (t.palette.mode === "dark" ? "#FDA29B" : "#B42318") }}>
      {children}
    </Typography>
  );
}

FieldError.propTypes = { children: PropTypes.node };

// ---------------------------------------------------------------------------
// Images: a preview drawn at the size the website shows it, so the editor
// sees what visitors will see. Broken images show a neutral placeholder.

export function ImagePreview({ src, width, height, fit = "contain", background, alt = "", rounded = 8, border = true }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  return (
    <Box
      sx={{
        width,
        height,
        borderRadius: `${rounded}px`,
        border: border ? 1 : 0,
        borderColor: "divider",
        bgcolor: background || "background.paper",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        color: "text.secondary",
        flexShrink: 0,
      }}
    >
      {src && !broken ? (
        <Box component="img" src={src} alt={alt} onError={() => setBroken(true)} sx={{ width: "100%", height: "100%", objectFit: fit }} />
      ) : (
        <Stack alignItems="center" spacing={0.25} sx={{ px: 0.5, textAlign: "center" }}>
          <IconPhoto size={Math.min(20, height / 2)} aria-hidden />
          {height >= 56 && (
            <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
              {src ? "Image missing" : "No image"}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}

ImagePreview.propTypes = {
  src: PropTypes.string,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  fit: PropTypes.oneOf(["contain", "cover"]),
  background: PropTypes.string,
  alt: PropTypes.string,
  rounded: PropTypes.number,
  border: PropTypes.bool,
};

const MAX_BYTES = 10 * 1024 * 1024; // the API's upload limit
const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/svg+xml", "image/gif"];

// The pixel size of an image file or address, or null.
function measure(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img.naturalWidth ? { width: img.naturalWidth, height: img.naturalHeight } : null);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

// value: a stored path, a File, or "" for none.
export function ImageField({ label, optional, value, onChange, preview, hint, error, minWidth, canRemove = true }) {
  const inputRef = useRef(null);
  const labelId = useId();
  const [objectUrl, setObjectUrl] = useState("");
  const [size, setSize] = useState(null);
  const [problem, setProblem] = useState("");
  useEffect(() => {
    if (!isFile(value)) {
      setObjectUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(value);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);
  const src = isFile(value) ? objectUrl : imageUrl(value);
  useEffect(() => {
    let live = true;
    setSize(null);
    if (src) measure(src).then((s) => live && setSize(s));
    return () => {
      live = false;
    };
  }, [src]);
  const pick = (file) => {
    if (!file) return;
    if (!IMAGE_TYPES.includes(file.type)) {
      setProblem("Choose a PNG, JPG, WebP, SVG or GIF image.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setProblem("This image is over 10 MB. Choose a smaller file.");
      return;
    }
    setProblem("");
    onChange(file);
  };
  const small = minWidth && size && !/svg/.test(isFile(value) ? value.type : String(value)) && size.width < minWidth;
  return (
    <Box>
      <FieldLabel id={labelId} optional={optional}>
        {label}
      </FieldLabel>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
        <Box
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            pick(e.dataTransfer.files?.[0]);
          }}
        >
          {preview(src)}
        </Box>
        <Stack spacing={1} alignItems="flex-start">
          <input
            ref={inputRef}
            type="file"
            accept={IMAGE_TYPES.join(",")}
            hidden
            aria-label={`${typeof label === "string" ? label : "Image"} file`}
            onChange={(e) => {
              pick(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <ActionButton
              startIcon={<IconUpload size={16} />}
              onClick={() => inputRef.current?.click()}
              aria-invalid={error ? "true" : "false"}
              aria-label={`${value ? "Replace" : "Choose"} the ${typeof label === "string" ? label.toLowerCase() : "image"}`}
            >
              {value ? "Replace image" : "Choose an image"}
            </ActionButton>
            {value && canRemove && (
              <ActionButton variant="text" tone="destructive" onClick={() => onChange("")}>
                Remove
              </ActionButton>
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {isFile(value) ? `${value.name} · not saved yet` : hint}
          </Typography>
          {size && (
            <Typography variant="caption" color="text.secondary">
              {size.width} × {size.height} px
            </Typography>
          )}
          {small && (
            <Typography variant="body2" color="warning.dark">
              This image is {size.width} px wide and may look blurry. Use one at least {minWidth} px wide.
            </Typography>
          )}
          <FieldError>{problem || error}</FieldError>
        </Stack>
      </Stack>
    </Box>
  );
}

ImageField.propTypes = {
  label: PropTypes.node.isRequired,
  optional: PropTypes.bool,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  preview: PropTypes.func.isRequired,
  hint: PropTypes.node,
  error: PropTypes.string,
  minWidth: PropTypes.number,
  canRemove: PropTypes.bool,
};

// A required "I checked" box for anything that makes a claim about real
// people or organisations.
export function HonestyCheck({ checked, onChange, error, children }) {
  const id = useId();
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: "14px",
        border: 1,
        borderColor: error ? "error.main" : "divider",
        bgcolor: "action.hover",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box
          component="input"
          type="checkbox"
          id={id}
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          aria-invalid={error ? "true" : "false"}
          sx={{ width: 20, height: 20, mt: "2px", flexShrink: 0, accentColor: (t) => t.palette.primary.main }}
        />
        <Box component="label" htmlFor={id} sx={{ cursor: "pointer" }}>
          <Typography variant="body2">{children}</Typography>
        </Box>
      </Stack>
      <FieldError>{error}</FieldError>
    </Box>
  );
}

HonestyCheck.propTypes = { checked: PropTypes.bool, onChange: PropTypes.func.isRequired, error: PropTypes.string, children: PropTypes.node };
